import React, { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { useCachedMedia } from "@/lib/mediaCache";
import { BlurImage } from "@/lib/blur";

const fmt = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

// Stable pseudo-random waveform bar heights (0.25–1.0)
const WAVE = Array.from({ length: 38 }, (_, i) => {
  const v = Math.abs(Math.sin(i * 1.7) * Math.cos(i * 0.6));
  return 0.25 + v * 0.75;
});

export function AudioPlayer({ src, mine }) {
  const audioRef = useRef(null);
  const cachedSrc = useCachedMedia(src);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [rate, setRate] = useState(1);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCur(a.currentTime);
    const onMeta = () => {
      if (a.duration === Infinity || isNaN(a.duration)) {
        a.currentTime = 1e101;
        a.ontimeupdate = () => {
          a.ontimeupdate = onTime;
          a.currentTime = 0;
          setDur(a.duration);
        };
      } else {
        setDur(a.duration);
      }
    };
    const onEnd = () => { setPlaying(false); setCur(0); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, [src]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().then(() => setPlaying(true)).catch(() => setPlaying(false)); }
  };

  const cycleRate = () => {
    const rates = [1, 1.5, 2];
    const next = rates[(rates.indexOf(rate) + 1) % rates.length];
    setRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const progress = dur ? cur / dur : 0;
  // Precise seek: jump to wherever the user clicks on the bar.
  const seekTo = (e) => {
    const a = audioRef.current;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * dur;
    setCur(ratio * dur);
  };

  return (
    <div className="flex items-center gap-2.5 min-w-[220px] max-w-[280px] py-0.5" data-testid="audio-player">
      <audio ref={audioRef} src={cachedSrc} preload="metadata" className="hidden" />
      <button onClick={toggle} data-testid="audio-play-toggle"
        className="shrink-0 h-10 w-10 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black hover:bg-[var(--rc-gold-light)] transition-colors">
        {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-[2px] h-8 cursor-pointer" data-testid="audio-seek-bar" onClick={seekTo}>
          {WAVE.map((h, i) => {
            const filled = i / WAVE.length <= progress;
            return (
              <span key={i} style={{ height: `${h * 100}%` }}
                className={`flex-1 rounded-full transition-colors pointer-events-none ${filled ? "bg-[var(--rc-gold)]" : (mine ? "bg-[var(--rc-gold)]/25" : "bg-[var(--rc-muted)]/40")}`} />
            );
          })}
        </div>
        <div className="mt-1 text-[10px] text-[var(--rc-muted)] tabular-nums">{fmt(playing || cur ? cur : dur)}</div>
      </div>
      <button onClick={cycleRate} data-testid="audio-speed-btn"
        className="shrink-0 h-7 min-w-[34px] px-1.5 grid place-items-center rounded-full text-[11px] font-semibold bg-[var(--rc-surface-2)] text-[var(--rc-gold)] hover:bg-[var(--rc-gold)]/15 transition-colors">
        {rate}x
      </button>
    </div>
  );
}

// Video thumbnail (first frame) + play badge — tapping opens the full-screen viewer.
export function VideoThumb({ src, poster, hash, onOpen }) {
  const vRef = useRef(null);
  const [dur, setDur] = useState(0);

  useEffect(() => {
    if (poster) return; // poster-first: don't download the video just to grab a frame
    const v = vRef.current;
    if (!v) return;
    const onMeta = () => { setDur(v.duration); try { v.currentTime = 0.1; } catch (e) {} };
    v.addEventListener("loadedmetadata", onMeta);
    return () => v.removeEventListener("loadedmetadata", onMeta);
  }, [src, poster]);

  return (
    <button onClick={onOpen} data-testid="video-thumb" className="relative block rounded-xl overflow-hidden max-h-72">
      {poster ? (
        <BlurImage src={poster} hash={hash} className="max-h-72" imgClassName="w-full max-h-72 object-cover bg-black" />
      ) : (
        <video ref={vRef} src={src} muted preload="none" playsInline
          className="w-full max-h-72 object-cover bg-black pointer-events-none min-h-[140px]" />
      )}
      <span className="absolute inset-0 grid place-items-center">
        <span className="h-14 w-14 grid place-items-center rounded-full bg-black/50 backdrop-blur-sm text-white border border-white/20">
          <Play size={24} className="ml-1" />
        </span>
      </span>
      {dur > 0 && !poster && (
        <span className="absolute bottom-2 right-2 text-[10px] text-white bg-black/50 rounded px-1.5 py-0.5 tabular-nums">{fmt(dur)}</span>
      )}
    </button>
  );
}
